import React, { useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useRenewal } from "../RenewalContext";
import { Card, Notice, StepHeading, StepNav } from "../renewalUi";
import PhoneVerification from "../../../components/PhoneVerification";
import { isRenewalTestMode } from "../renewalTestMode";

interface Props {
  onBack: () => void;
  onNext: () => void;
}

/**
 * Contact confirmation — identical across all three flows.
 *
 * Email is shown as already verified: the tenant reached this page by logging
 * in, and login sends a code to that address. The phone needs an SMS
 * round-trip (Twilio Verify) before they can continue — verification is
 * mandatory here.
 *
 * A number that is already verified for this user skips the round-trip
 * entirely: on mount we ask the API, and `Send` short-circuits server-side
 * with `codeSent: false` if it turns out to be verified already. Editing the
 * field clears the verified flag, so a NEW number always gets verified.
 *
 * In test mode (`/renewal/test`) there's no session, so the API isn't called
 * and any 6 digits pass — the click-through has to work without a backend.
 */
const ContactStep: React.FC<Props> = ({ onBack, onNext }) => {
  const { user } = useAuth();
  const { draft, update, termUuid } = useRenewal();
  const { contact } = draft;

  const testMode = isRenewalTestMode(termUuid);

  // Seed from the logged-in user the first time this step mounts.
  useEffect(() => {
    if (contact.name || contact.email || contact.phone) return;
    update({
      contact: {
        name:
          [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
          "",
        email: user?.email ?? "",
        phone: user?.phone ?? "",
        phoneVerified: false,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setContact = (patch: Partial<typeof contact>) =>
    update({ contact: { ...contact, ...patch } });

  return (
    <>
      <StepHeading
        title="Confirm your contact info"
        subtitle="We'll use this to follow up with you."
      />

      <Card>
        <div className="mb-4">
          <label className="mb-1 block text-sm text-gray-600">Name</label>
          <input
            type="text"
            value={contact.name}
            onChange={(e) => setContact({ name: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-sm text-gray-600">Email</label>
          <div className="relative">
            <input
              type="email"
              value={contact.email}
              onChange={(e) => setContact({ email: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-24 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success">
              ✓ Verified
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-sm text-gray-600">Phone</label>
          <div className="relative">
            <input
              type="tel"
              value={contact.phone}
              onChange={(e) =>
                setContact({ phone: e.target.value, phoneVerified: false })
              }
              className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-24 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {contact.phoneVerified && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success">
                ✓ Verified
              </span>
            )}
          </div>
        </div>

        {!contact.phoneVerified && (
          <Notice tone="warning" title="Phone verification is required to proceed">
            <PhoneVerification
              phone={contact.phone}
              verified={contact.phoneVerified}
              onVerifiedChange={(v) => setContact({ phoneVerified: v })}
              onPhoneNormalized={(p) => setContact({ phone: p })}
              testMode={testMode}
            />
          </Notice>
        )}

      </Card>

      <StepNav
        onBack={onBack}
        onNext={onNext}
        nextLabel="Looks Good →"
        nextDisabled={!contact.phoneVerified}
      />
    </>
  );
};

export default ContactStep;
